import type { PDFDocumentProxy, TextItem } from 'pdfjs-dist/types/src/display/api';

import type { DocumentTableCell } from '@/shared/components/tables/DocumentTable/model';

import { buildPdfSemanticGroups, PdfSemanticLine } from './pdfTableHeuristics';

export type ExtractedDocumentBlockKind = 'heading' | 'list-item' | 'paragraph' | 'quote' | 'table';
export type ExtractedDocumentSourceType = 'docx' | 'pdf' | 'text';

export interface ExtractedSourceRectangle {
    height: number;
    page: number;
    width: number;
    x: number;
    y: number;
}

export interface ExtractedSourceSegment {
    blockEnd: number;
    blockStart: number;
    itemIndex?: number;
    page: number;
    sourceEnd: number;
    sourceStart: number;
    direction?: string;
    rectangle?: ExtractedSourceRectangle;
}

export type ExtractedDocumentTableCell = DocumentTableCell;

export interface ExtractedDocumentBlock {
    id: string;
    kind: ExtractedDocumentBlockKind;
    level?: number;
    ordered?: boolean;
    page: number;
    segments: ExtractedSourceSegment[];
    tableRows?: ExtractedDocumentTableCell[][];
    text: string;
}

export interface ExtractedDocument {
    blocks: ExtractedDocumentBlock[];
    mimeType: string;
    name: string;
    pageCount: number;
    plainText: string;
    sourceType: ExtractedDocumentSourceType;
    status: 'empty' | 'ocr-required' | 'ready' | 'unsupported';
    warnings: string[];
    wordCount: number;
}

export interface ExtractedDocumentTextItem {
    direction: string;
    end: number;
    hasEol: boolean;
    height: number;
    index: number;
    page: number;
    start: number;
    text: string;
    width: number;
    x: number;
    y: number;
}

export interface ExtractedDocumentPage {
    items: ExtractedDocumentTextItem[];
    page: number;
    text: string;
}

export interface ExtractedTextPosition {
    blockId: string;
    offset: number;
}

export interface ExtractedSelectionSourceReference {
    blockEnd: number;
    blockId: string;
    blockStart: number;
    itemIndex?: number;
    page: number;
    sourceEnd: number;
    sourceStart: number;
}

export interface ExtractedTextSelection {
    contextAfter: string;
    contextBefore: string;
    end: ExtractedTextPosition;
    quote: string;
    sourceRectangles: ExtractedSourceRectangle[];
    sourceReferences: ExtractedSelectionSourceReference[];
    start: ExtractedTextPosition;
}

const DOCUMENT_WORD_PATTERN = /[\p{L}\p{N}][\p{L}\p{N}'’.-]*/gu;
const LIST_ITEM_PATTERN = /^\s*(?:[-–—•*]|\d+[.)]|[A-Za-z][.)])\s+/;
const QUOTE_PATTERN = /^\s*(?:>|[“"])/;

const multiplyTransforms = (left: number[], right: number[]) => [
    left[0] * right[0] + left[2] * right[1],
    left[1] * right[0] + left[3] * right[1],
    left[0] * right[2] + left[2] * right[3],
    left[1] * right[2] + left[3] * right[3],
    left[0] * right[4] + left[2] * right[5] + left[4],
    left[1] * right[4] + left[3] * right[5] + left[5],
];

const stableBlockId = (source: ExtractedDocumentSourceType, page: number, index: number, kind: string) =>
    `${source}:${page}:${kind}:${index}`;

const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim();

const getWordCount = (text: string) => [...text.matchAll(DOCUMENT_WORD_PATTERN)].length;

export const extractPdfTextPages = async (
    pdf: PDFDocumentProxy,
    signal?: AbortSignal,
): Promise<ExtractedDocumentPage[]> => {
    const pages: ExtractedDocumentPage[] = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        if (signal?.aborted) {
            throw new DOMException('Document extraction was aborted.', 'AbortError');
        }

        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const textContent = await page.getTextContent();
        let text = '';
        const items: ExtractedDocumentTextItem[] = [];

        textContent.items.forEach((candidate) => {
            if (!('str' in candidate) || !candidate.str.trim()) {
                return;
            }

            const item: TextItem = candidate;
            const separator = text && !text.endsWith(' ') ? ' ' : '';

            text += separator;
            const start = text.length;

            text += item.str;
            const transform = multiplyTransforms(viewport.transform, item.transform);
            const fontHeight = Math.hypot(transform[2], transform[3]);

            items.push({
                direction: item.dir,
                end: text.length,
                hasEol: item.hasEOL,
                height: Math.max(fontHeight / viewport.height, 0.006),
                index: items.length,
                page: pageNumber,
                start,
                text: item.str,
                width: Math.min(Math.max((item.width * viewport.scale) / viewport.width, 0.008), 1),
                x: Math.min(Math.max(transform[4] / viewport.width, 0), 1),
                y: Math.min(Math.max((transform[5] - fontHeight) / viewport.height, 0), 1),
            });
        });
        pages.push({ items, page: pageNumber, text });
    }

    return pages;
};

const sourceSegmentsForLine = (line: PdfSemanticLine, initialOffset = 0) => {
    let blockOffset = 0;

    return line.items.map((item, index): ExtractedSourceSegment => {
        const prefix = index ? ' ' : '';
        const blockStart = initialOffset + blockOffset + prefix.length;

        blockOffset += prefix.length + item.text.length;
        return {
            blockEnd: initialOffset + blockOffset,
            blockStart,
            direction: item.direction,
            itemIndex: item.index,
            page: item.page,
            rectangle: {
                height: item.height,
                page: item.page,
                width: item.width,
                x: item.x,
                y: item.y,
            },
            sourceEnd: item.end,
            sourceStart: item.start,
        };
    });
};

const sourceSegmentsForTable = (rows: PdfSemanticLine[][]) => {
    const segments: ExtractedSourceSegment[] = [];
    let offset = 0;

    rows.forEach((row, rowIndex) => {
        if (rowIndex) {
            offset += 1;
        }
        row.forEach((cell, cellIndex) => {
            if (cellIndex) {
                offset += 1;
            }
            segments.push(...sourceSegmentsForLine(cell, offset));
            offset += cell.text.length;
        });
    });

    return segments;
};

const firstPdfTableRowIsHeader = (rows: PdfSemanticLine[][]) => {
    const firstRow = rows[0] ?? [];
    const remainingRows = rows.slice(1).flat();
    const firstRowHasLabels = firstRow.some((cell) => /\p{L}/u.test(cell.text));
    const remainingRowsHaveValues = remainingRows.some((cell) => /\d/u.test(cell.text));
    const firstHeight = firstRow.reduce((total, cell) => total + cell.height, 0) / Math.max(firstRow.length, 1);
    const remainingHeight =
        remainingRows.reduce((total, cell) => total + cell.height, 0) / Math.max(remainingRows.length, 1);

    return firstRowHasLabels && (remainingRowsHaveValues || firstHeight >= remainingHeight * 1.08);
};

export const buildPdfExtractedDocument = (
    pages: ExtractedDocumentPage[],
    input: { mimeType: string; name: string },
): ExtractedDocument => {
    const groups = pages.flatMap(buildPdfSemanticGroups);
    const lines = groups.flatMap((group) => group.rows.flat());
    const heights = lines.map((line) => line.height).sort((left, right) => left - right);
    const medianHeight = heights[Math.floor(heights.length / 2)] ?? 0.012;
    const blocks = groups.map((group, index): ExtractedDocumentBlock => {
        if (group.kind === 'table') {
            const headerRow = firstPdfTableRowIsHeader(group.rows);
            const tableRows = group.rows.map((row, rowIndex) =>
                row.map((cell) => ({
                    colspan: 1,
                    header: headerRow && rowIndex === 0 ? true : undefined,
                    rowspan: 1,
                    text: cell.text,
                })),
            );

            return {
                id: stableBlockId('pdf', group.page, index, 'table'),
                kind: 'table',
                page: group.page,
                segments: sourceSegmentsForTable(group.rows),
                tableRows,
                text: group.rows.map((row) => row.map((cell) => cell.text).join('\t')).join('\n'),
            };
        }

        const line = group.rows[0][0];
        const listMatch = line.text.match(LIST_ITEM_PATTERN);
        const headingRatio = line.height / Math.max(medianHeight, 0.001);
        const looksLikeHeading = line.text.length <= 160 && headingRatio >= 1.18;
        const kind: ExtractedDocumentBlockKind = listMatch
            ? 'list-item'
            : QUOTE_PATTERN.test(line.text)
              ? 'quote'
              : looksLikeHeading
                ? 'heading'
                : 'paragraph';

        return {
            id: stableBlockId('pdf', line.page, index, kind),
            kind,
            level: kind === 'heading' ? (headingRatio >= 1.6 ? 1 : headingRatio >= 1.35 ? 2 : 3) : undefined,
            ordered: kind === 'list-item' ? /^\s*(?:\d+|[A-Za-z])[.)]/.test(line.text) : undefined,
            page: line.page,
            segments: sourceSegmentsForLine(line),
            text: line.text,
        };
    });
    const plainText = blocks.map((block) => block.text).join('\n\n');
    const wordCount = getWordCount(plainText);

    return {
        blocks,
        mimeType: input.mimeType,
        name: input.name,
        pageCount: pages.length,
        plainText,
        sourceType: 'pdf',
        status: wordCount ? 'ready' : 'ocr-required',
        warnings: wordCount ? [] : ['No embedded text was found. OCR is required for this document.'],
        wordCount,
    };
};

const getElementKind = (element: HTMLElement): ExtractedDocumentBlockKind => {
    const tagName = element.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tagName)) {
        return 'heading';
    }
    if (tagName === 'li') {
        return 'list-item';
    }
    if (tagName === 'blockquote') {
        return 'quote';
    }
    if (tagName === 'table') {
        return 'table';
    }
    return 'paragraph';
};

export const buildDocxExtractedDocument = (
    container: HTMLElement,
    input: { mimeType: string; name: string },
): ExtractedDocument => {
    const sections = Array.from(container.querySelectorAll<HTMLElement>('section.attachment-docx'));
    const pages = sections.length ? sections : [container];
    const blocks: ExtractedDocumentBlock[] = [];

    pages.forEach((pageElement, pageIndex) => {
        const elements = Array.from(
            pageElement.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6,p,li,blockquote,table'),
        ).filter((element) => element.tagName === 'TABLE' || !element.closest('table'));

        elements.forEach((element) => {
            const text = normalizeText(element.textContent ?? '');

            if (!text) {
                return;
            }
            const kind = getElementKind(element);
            const blockIndex = blocks.length;
            const tableRows =
                kind === 'table'
                    ? Array.from(element.querySelectorAll('tr')).map((row) =>
                          Array.from(row.querySelectorAll('th,td')).map((cell) => ({
                              colspan: Number(cell.getAttribute('colspan') ?? 1),
                              header: cell.tagName === 'TH' ? true : undefined,
                              rowspan: Number(cell.getAttribute('rowspan') ?? 1),
                              text: normalizeText(cell.textContent ?? ''),
                          })),
                      )
                    : undefined;

            blocks.push({
                id: stableBlockId('docx', pageIndex + 1, blockIndex, kind),
                kind,
                level: kind === 'heading' ? Number(element.tagName.slice(1)) : undefined,
                ordered: kind === 'list-item' ? element.closest('ol') !== null : undefined,
                page: pageIndex + 1,
                segments: [
                    {
                        blockEnd: text.length,
                        blockStart: 0,
                        page: pageIndex + 1,
                        sourceEnd: text.length,
                        sourceStart: 0,
                    },
                ],
                tableRows,
                text,
            });
        });
    });
    const plainText = blocks.map((block) => block.text).join('\n\n');
    const wordCount = getWordCount(plainText);

    return {
        blocks,
        mimeType: input.mimeType,
        name: input.name,
        pageCount: pages.length,
        plainText,
        sourceType: 'docx',
        status: wordCount ? 'ready' : 'empty',
        warnings: wordCount ? [] : ['No readable text was found in this DOCX file.'],
        wordCount,
    };
};

export const buildTextExtractedDocument = (
    text: string,
    input: { mimeType: string; name: string },
): ExtractedDocument => {
    const chunks = text
        .split(/\n{2,}/)
        .map(normalizeText)
        .filter(Boolean);
    const blocks = chunks.map(
        (chunk, index): ExtractedDocumentBlock => ({
            id: stableBlockId('text', 1, index, 'paragraph'),
            kind: 'paragraph',
            page: 1,
            segments: [{ blockEnd: chunk.length, blockStart: 0, page: 1, sourceEnd: chunk.length, sourceStart: 0 }],
            text: chunk,
        }),
    );
    const plainText = blocks.map((block) => block.text).join('\n\n');
    const wordCount = getWordCount(plainText);

    return {
        blocks,
        mimeType: input.mimeType,
        name: input.name,
        pageCount: 1,
        plainText,
        sourceType: 'text',
        status: wordCount ? 'ready' : 'empty',
        warnings: wordCount ? [] : ['No readable text was found in this file.'],
        wordCount,
    };
};

export const sourceRectanglesForSelection = (
    block: ExtractedDocumentBlock,
    start: number,
    end: number,
): ExtractedSourceRectangle[] =>
    block.segments.flatMap((segment) => {
        if (!segment.rectangle || segment.blockEnd <= start || segment.blockStart >= end) {
            return [];
        }
        const segmentLength = Math.max(segment.blockEnd - segment.blockStart, 1);
        const relativeStart = (Math.max(start, segment.blockStart) - segment.blockStart) / segmentLength;
        const relativeEnd = (Math.min(end, segment.blockEnd) - segment.blockStart) / segmentLength;
        const rectangle = segment.rectangle;
        const x =
            segment.direction === 'rtl'
                ? rectangle.x + rectangle.width * (1 - relativeEnd)
                : rectangle.x + rectangle.width * relativeStart;

        return [
            {
                ...rectangle,
                width: rectangle.width * Math.max(relativeEnd - relativeStart, 0),
                x,
            },
        ];
    });

export const sourceReferencesForSelection = (
    block: ExtractedDocumentBlock,
    start: number,
    end: number,
): ExtractedSelectionSourceReference[] =>
    block.segments.flatMap((segment) => {
        const blockStart = Math.max(start, segment.blockStart);
        const blockEnd = Math.min(end, segment.blockEnd);

        if (blockEnd <= blockStart) {
            return [];
        }
        const segmentLength = Math.max(segment.blockEnd - segment.blockStart, 1);
        const sourceLength = Math.max(segment.sourceEnd - segment.sourceStart, 0);
        const relativeStart = (blockStart - segment.blockStart) / segmentLength;
        const relativeEnd = (blockEnd - segment.blockStart) / segmentLength;

        return [
            {
                blockEnd,
                blockId: block.id,
                blockStart,
                itemIndex: segment.itemIndex,
                page: segment.page,
                sourceEnd: Math.round(segment.sourceStart + sourceLength * relativeEnd),
                sourceStart: Math.round(segment.sourceStart + sourceLength * relativeStart),
            },
        ];
    });
